import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Enrichment = () => {
  const programs = [
    {
      category: "Sports & Athletics",
      description: "Building teamwork, physical fitness, and sportsmanship through competitive and recreational activities.",
      activities: [
        {
          name: "Basketball Club",
          description: "Developing basketball skills, teamwork, and strategic thinking through practice and friendly competitions.",
          ages: "Grades 2-5",
          schedule: "After school, Tuesdays & Thursdays",
          benefits: ["Physical fitness", "Teamwork", "Leadership skills", "Strategic thinking"]
        },
        {
          name: "Soccer Club", 
          description: "Learning soccer fundamentals while building endurance, coordination, and team collaboration.",
          ages: "Grades 2-5",
          schedule: "After school, Mondays & Wednesdays", 
          benefits: ["Cardiovascular health", "Coordination", "Team spirit", "Goal setting"]
        },
        {
          name: "T-Ball",
          description: "Introduction to baseball fundamentals in a fun, supportive environment for our younger students.",
          ages: "Grades K-2",
          schedule: "After school, Fridays",
          benefits: ["Hand-eye coordination", "Basic motor skills", "Following directions", "Confidence building"]
        }
      ]
    },
    {
      category: "Arts & Performance",
      description: "Fostering creativity, self-expression, and artistic talents through various mediums and performances.",
      activities: [
        {
          name: "Theater Club (Shakespeare Production)",
          description: "Exploring classic literature through dramatic performance, building confidence and public speaking skills.",
          ages: "Grades 3-5",
          schedule: "After school, Tuesdays & Thursdays",
          benefits: ["Public speaking", "Literary appreciation", "Confidence", "Creative expression"]
        },
        {
          name: "Art Club",
          description: "Exploring various artistic mediums including painting, drawing, sculpture, and mixed media projects.",
          ages: "Grades K-5",
          schedule: "After school, Mondays & Wednesdays",
          benefits: ["Creative expression", "Fine motor skills", "Visual arts appreciation", "Self-confidence"]
        },
        {
          name: "Hip Hop Dance",
          description: "Learning contemporary dance moves while building rhythm, coordination, and performance confidence.",
          ages: "Grades 2-5", 
          schedule: "After school, Fridays",
          benefits: ["Physical fitness", "Rhythm", "Self-expression", "Cultural appreciation"]
        }
      ]
    },
    {
      category: "STEAM & Innovation",
      description: "Encouraging scientific thinking, technology skills, and innovative problem-solving approaches.",
      activities: [
        {
          name: "Nancy Drew Coding Club",
          description: "Introduction to computer programming through mystery-solving games and interactive coding challenges.",
          ages: "Grades 3-5",
          schedule: "After school, Tuesdays & Thursdays", 
          benefits: ["Logical thinking", "Problem solving", "Technology literacy", "Critical thinking"]
        },
        {
          name: "Gardening Club",
          description: "Learning about plant biology, sustainable practices, and environmental stewardship through hands-on gardening.",
          ages: "Grades K-5",
          schedule: "After school, Mondays & Wednesdays",
          benefits: ["Environmental awareness", "Scientific observation", "Responsibility", "Healthy eating habits"]
        }
      ]
    },
    {
      category: "Media & Communication",
      description: "Developing communication skills, media literacy, and journalistic thinking through real-world projects.",
      activities: [
        {
          name: "P.S. 11x News Team",
          description: "Creating school news content, conducting interviews, and learning about journalism and media production.",
          ages: "Grades 3-5",
          schedule: "After school, Mondays & Fridays",
          benefits: ["Communication skills", "Research abilities", "Media literacy", "Leadership"]
        }
      ]
    }
  ];

  return (
    <main id="main-content" className="bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                After School Programs
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                Enrichment <span className="font-bold">Opportunities</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Discover your talents and explore new interests through our diverse after-school programs designed to inspire creativity, build skills, and foster personal growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Discover Your <span className="font-bold text-[#630503]">Passion</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our enrichment programs provide students with opportunities to explore interests beyond the classroom, develop new skills, and build lasting friendships.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
            <div className="text-center">
              <div className="text-4xl font-light text-[#630503] mb-2">9</div>
              <div className="text-sm uppercase tracking-wider text-gray-600 font-medium">Programs Offered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-[#630503] mb-2">4</div>
              <div className="text-sm uppercase tracking-wider text-gray-600 font-medium">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-[#630503] mb-2">200+</div>
              <div className="text-sm uppercase tracking-wider text-gray-600 font-medium">Students Participating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-[#630503] mb-2">15+</div>
              <div className="text-sm uppercase tracking-wider text-gray-600 font-medium">Dedicated Staff</div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Categories */}
      {programs.map((category, categoryIndex) => (
        <section key={categoryIndex} className={`py-20 ${categoryIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Header */}
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-6">
                <span className="font-bold text-[#630503]">{category.category}</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {category.description}
              </p>
            </div>

            {/* Activities Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {category.activities.map((activity, activityIndex) => (
                <div key={activityIndex} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="p-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">{activity.name}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{activity.description}</p>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-900 w-20">Ages:</span>
                        <span className="text-gray-600">{activity.ages}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-900 w-20">Schedule:</span>
                        <span className="text-gray-600">{activity.schedule}</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Benefits</h4>
                      <div className="flex flex-wrap gap-2">
                        {activity.benefits.map((benefit, benefitIndex) => (
                          <span key={benefitIndex} className="inline-block bg-[#630503]/10 text-[#630503] px-3 py-1 rounded-full text-xs font-medium">
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Registration Information */}
      <section className="py-20 bg-[#630503]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              Ready to <span className="font-bold">Get Started?</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-12">
              Registration for enrichment programs opens at the beginning of each semester. Contact our main office for more information or to sign up your child.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Fall Registration</h3>
                <p className="text-white/80 mb-2">September 1-15</p>
                <p className="text-white/80">Programs begin September 18</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Spring Registration</h3>
                <p className="text-white/80 mb-2">January 15-30</p>
                <p className="text-white/80">Programs begin February 5</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-[#630503] hover:bg-white/90 font-semibold px-8 py-4 text-base transition-colors rounded-lg">
                Register Now
              </button>
              <button className="bg-white/10 border border-white/30 text-white hover:bg-white/20 font-semibold px-8 py-4 text-base transition-colors rounded-lg">
                Contact Main Office
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Questions About <span className="font-bold text-[#630503]">Our Programs?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
              Our enrichment coordinators are here to help you find the perfect program for your child's interests and schedule.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="bg-[#630503]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#630503]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Call Us</h3>
                <p className="text-gray-600">(718) 329-8000</p>
              </div>
              
              <div className="text-center">
                <div className="bg-[#630503]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#630503]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email Us</h3>
                <p className="text-gray-600">enrichment@ps011x.org</p>
              </div>
              
              <div className="text-center">
                <div className="bg-[#630503]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#630503]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Visit Us</h3>
                <p className="text-gray-600">Main Office, Room 101</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Enrichment; 